import React from 'react'
import { SpotifyPreviewContent } from './SpotifyPreviewContent'
import { YouTubePreviewContent } from './YouTubePreviewContent'
import { PDFPreviewContent } from './PDFPreviewContent'
import { MultiShopPreviewContent } from './MultiShopPreviewContent'
import { AuthorPreviewContent } from './AuthorPreviewContent'
import { BookSamplePreviewContent } from './BookSamplePreviewContent'
import { WebsitePreviewContent } from './WebsitePreviewContent'

interface UnifiedPreviewProps {
  type: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
  data: any
  brandingType?: 'beyondpages' | 'custom' | 'none'
  customBranding?: {
    companyName?: string
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
    textColor?: string
    displayType?: 'logo_only' | 'name_only' | 'logo_and_name'
  }
  colorMode?: 'gradient' | 'structured'
}

export const UnifiedPreview: React.FC<UnifiedPreviewProps> = ({ 
  type, 
  data, 
  brandingType = 'beyondpages',
  customBranding,
  colorMode = 'gradient'
}) => {
  // Determine colors - use data.customColors if available, otherwise fall back to customBranding
  const primaryColor = data.customColors?.primaryColor || customBranding?.primaryColor || '#93C5FD'
  const secondaryColor = data.customColors?.secondaryColor || customBranding?.secondaryColor || '#DBEAFE'
  const textColor = data.customColors?.textColor || customBranding?.textColor || '#1F2937'
  const wrapperColor = data.customColors?.wrapperColor || customBranding?.wrapperColor || '#5B8DD9'
  const wrapperOpacity = data.customColors?.wrapperOpacity || customBranding?.wrapperOpacity || 1
  
  // Background gradient for gradient mode
  const customGradient = colorMode === 'gradient'
    ? `linear-gradient(to bottom right, ${primaryColor}, ${secondaryColor})`
    : undefined

  // Prepare branding object for content components
  const branding = {
    companyName: brandingType === 'beyondpages' ? '' : 
                 brandingType === 'none' ? '' : 
                 customBranding?.companyName || '',
    brandingType: brandingType,
    primaryColor: primaryColor,
    secondaryColor: secondaryColor,
    textColor: textColor,
    wrapperColor: wrapperColor,
    wrapperOpacity: wrapperOpacity,
    colorMode: colorMode
  }

  // Prepare customColors structure for components expecting it directly
  const customColors = {
    primaryColor,
    secondaryColor,
    textColor,
    wrapperColor,
    wrapperOpacity
  }

  // Main render without any device frame - BasePreview handles that
  return (
    <>
      {type === 'spotify' && (
        <SpotifyPreviewContent
          data={data}
          customColors={customColors}
          colorMode={colorMode}
          brandingType={brandingType}
          userBranding={brandingType === 'custom' ? {
            companyName: customBranding?.companyName,
            logoUrl: customBranding?.logoUrl,
            primaryColor,
            secondaryColor,
            textColor,
            displayType: customBranding?.displayType
          } : undefined}
          isPreview={true}
        />
      )}
      {type === 'youtube' && (
        <YouTubePreviewContent
          data={data}
          branding={branding}
          bgGradient={customGradient}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
      {type === 'pdf' && (
        <PDFPreviewContent 
          data={data} 
          branding={branding}
          bgGradient={customGradient}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
      {type === 'multishop' && (
        <MultiShopPreviewContent
          data={data}
          branding={branding}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
      {type === 'author' && (
        <AuthorPreviewContent 
          data={data} 
          branding={branding}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
      {type === 'sample' && (
        <BookSamplePreviewContent 
          data={data} 
          branding={branding}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
      {type === 'website' && (
        <WebsitePreviewContent
          data={data}
          branding={branding}
          bgGradient={customGradient}
          colorMode={colorMode}
          isPreview={true}
        />
      )}
    </>
  )
} 
